import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const adminApi = axios.create({
  baseURL: API_BASE_URL,
});

// Admin Auth Interceptor (Google OAuth ID Token)
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

export const adminService = {
  // Metadata Management
  updateSongMetadata: async (slug, data) => {
    const response = await adminApi.patch(`/admin/songs/${slug}/metadata`, data);
    return response.data;
  },

  createSong: async (data) => {
    const response = await adminApi.post('/admin/songs', data);
    return response.data;
  },

  deleteSong: async (slug) => {
    const response = await adminApi.delete(`/admin/songs/${slug}`);
    return response.data;
  },

  // Public Search (Admin can also search)
  searchSongs: async (query) => {
    // We use the adminApi instance to ensure interceptors (auth) are applied
    // But choir search is usually at /choir/search relative to /api/v1
    const response = await adminApi.get('/choir/search', { params: { q: query } });
    return response.data;
  },

  getSongDetail: async (slug) => {
    const response = await adminApi.get(`/choir/songs/${slug}`);
    return response.data;
  },

  // Emergency Restoration
  revertToOriginal: async (slug) => {
    const response = await adminApi.post(`/admin/songs/${slug}/revert`);
    return response.data;
  },

  // Forensic Auditing
  getAuditLogs: async (params = {}) => {
    const response = await adminApi.get('/admin/audit-logs', { params });
    return response.data;
  },

  getRecentActivity: async (limit = 12) => {
    const response = await adminApi.get('/admin/stats/recent-activity', { params: { limit } });
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await adminApi.get('/admin/stats/dashboard');
    return response.data;
  },

  getTopSongsDetailed: async (limit = 5) => {
    const response = await adminApi.get('/admin/stats/top-songs-detailed', { params: { limit } });
    return response.data;
  },

  // File Proxy (CORS bypass)
  downloadFileProxy: async (url) => {
    const response = await adminApi.get('/admin/proxy-file', { 
      params: { url: url },
      responseType: 'arraybuffer' 
    });
    return response.data;
  },

  // Upload Management
  proxyUpload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await adminApi.post('/admin/proxy-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  requestUpload: async (fileName, fileType) => {
    const response = await adminApi.post('/admin/request-upload', null, { 
      params: { 
        file_name: fileName, 
        file_type: fileType 
      } 
    });
    return response.data;
  },

  // Add media record to database after successful B2 upload
  addMediaToSong: async (slug, mediaData) => {
    const response = await adminApi.post(`/admin/songs/${slug}/media`, mediaData);
    return response.data;
  },

  deleteMediaFromSong: async (slug, fileUrl) => {
    const response = await adminApi.delete(`/admin/songs/${slug}/media`, { 
      params: { file_url: fileUrl } 
    });
    return response.data;
  },

  // System Health
  checkHealth: async () => {
    const response = await axios.get('/health');
    return response.data;
  }
};

// Global Error Handler
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // If backend returns 401 (Unauthorized) or 403 (Forbidden)
    // it means the token is invalid or the user is not in the whitelist
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      localStorage.removeItem('admin_token');
      // We should NOT reload here as it causes an infinite loop
      // Instead, we can let the app state handle it or redirect
    }
    
    console.error('API Error:', error.response ? error.response.data : error.message);
    return Promise.reject(error);
  }
);
